import { ApplicationUserResponse } from "@api/apiClient";
import { useRoles } from "@api/roles";
import { useDeleteUser, useUsers } from "@api/users";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Snackbar, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowId } from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useResizeObserver from "use-resize-observer";
import PermissionsForm from "./PermissionsForm";

const UsersGrid = observer(() => {
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [openDeleteToast, setOpenDeleteToast] = useState(false);
  const [rows, setRows] = useState<ApplicationUserResponse[]>([]);
  const [openPermissionsForm, setOpenPermisisonsForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ApplicationUserResponse | null>(null);
  const users = useUsers();
  const roles = useRoles();
  const deleteUser = useDeleteUser();
  const navigate = useNavigate();
  const confirm = useConfirm();

  console.log("roles", roles);

  useEffect(() => {
    const newRows = users.data?.map((row: any, index: number) => ({ ...row, id: index })) || [];
    setRows(newRows);
  }, [users.data]);

  const handleEditClick = (row: any) => {
    navigate(`/settings/users/${row.userId}`);
  };

  const handleDeleteClick = async (id: GridRowId) => {
    const { confirmed } = await confirm({
      title: i18n.t("users-tab.confirm-dialog.header"),
      description: i18n.t("users-tab.confirm-dialog.message"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (confirmed) {
      const deletingRow = rows.find((row: any) => row.id === id);
      if (deletingRow?.userId) {
        await deleteUser.mutateAsync(deletingRow.userId as any);
        setOpenDeleteToast(true);
      }
    }
  };

  const handleOpenPermissionsForm = (id: GridRowId) => {
    setSelectedUser(rows.find((row: any) => row.id === id) || null);
    setOpenPermisisonsForm(true);
  };

  const handleClosePermissionsForm = () => {
    setOpenPermisisonsForm(false);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("admin-tab.grid.header-name"),
      editable: false,
      flex: 1,
    },
    {
      field: "email",
      headerName: i18n.t("admin-tab.grid.header-email"),
      editable: false,
      flex: 1,
    },
    {
      field: "roles",
      headerName: i18n.t("admin-tab.grid.header-roles"),
      editable: false,
      flex: 1,
      valueFormatter: ({ value }: { value: string[] }) => {
        return value?.join(", ");
      },
    },
    {
      field: "actions",
      type: "actions",
      width: 80,
      getActions: ({ id, row }) => {
        return [
          <GridActionsCellItem
            key={`edit-${id}`}
            icon={
              <Tooltip title={i18n.t("users-tab.grid.edit-icon-tooltip")}>
                <EditIcon />
              </Tooltip>
            }
            label={i18n.t("users-tab.grid.edit-icon-title")}
            disabled={true}
            onClick={() => handleEditClick(row)}
          />,
          /*
          NOTE: DeleteUser is not implemented in the API
          <GridActionsCellItem
            key={`delete-${id}`}
            icon={
              <Tooltip title={i18n.t("users-tab.grid.delete-icon-tooltip")}>
                <GridDeleteIcon />
              </Tooltip>
            }
            label={i18n.t("users-tab.grid.delete-icon-title")}
            onClick={() => handleDeleteClick(id)}
            disabled
          />,
          */
        ];
      },
    },
  ];

  return (
    <>
      <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
        <DataGrid
          loading={users.isFetching || users.isPending}
          columns={columns}
          rows={rows}
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [{ field: "email", sort: "asc" }],
            },
          }}
          sx={{ height: `${(height || 400) - 16}px` }}
        />
      </Box>
      <PermissionsForm user={selectedUser} open={openPermissionsForm} onClose={() => handleClosePermissionsForm()} />
      <Snackbar
        open={openDeleteToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("admin-tab.grid.delete-success-message")}
        onClose={() => setOpenDeleteToast(false)}
      />
    </>
  );
});

export default UsersGrid;
